import { Pencil, Plus, Search, Star, Trash } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import FooterSelectModalExample from "../../components/FooterSelectModal";
import MakeDefaultModal from "../../components/MakeDefaultModal";
import { setGlobalPopupElement } from "../../features/popupSlice";
import { CustomFooterItem } from "../../types/footer";
import { getImageUrl, toastMe } from "../../utils";
import {
    createCustomFooter,
    deleteCustomFooter,
    getAllCustomFooters,
    makeFooterDefault,
} from "../../utils/requests";

const FooterManagementPage: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // State management with proper types
    const [footers, setFooters] = useState<CustomFooterItem[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [newFooterName, setNewFooterName] = useState<string>("");
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(1);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    // Pagination settings
    const itemsPerPage: number = 5;

    const loadFooters = useCallback(async () => {
        setIsLoading(true);

        try {
            const response = await getAllCustomFooters({
                perPage: itemsPerPage,
                search: searchTerm,
                page: currentPage,
            });
            setFooters(response.data);
            setTotalPages(
                Math.ceil(response.page?.totalIndex / itemsPerPage)
            );
        } catch (error) {
            console.error("Error fetching headers:", error);
        } finally {
            setIsLoading(false);
        }
    }, [searchTerm, currentPage]);

    // Load footers effect
    useEffect(() => {
        loadFooters();
    }, [loadFooters]);

    // Handle create new footer
    const handleCreateFooter = async (e: React.FormEvent): Promise<void> => {
        e.preventDefault();
        if (!newFooterName.trim()) return;

        const createFooterFunc = async (footer?: CustomFooterItem) => {
            setIsSubmitting(true);

            try {
                const newFooter = await createCustomFooter({
                    name: newFooterName,
                    settings: footer?.settings
                });

                setFooters((prevFooters) => [...prevFooters, newFooter.data]);
                setNewFooterName("");

                toastMe(
                    "Footer created! You will be redirected to edit page.",
                    "success"
                );

                navigate(`/footer-builder/${newFooter.data._id}`);
            } catch (error) {
                console.error("Error creating footer:", error);
                toastMe("Failed to create footer. Please try again.", "error");
                setIsSubmitting(false);
            }
        }

        dispatch(setGlobalPopupElement(
            <FooterSelectModalExample
                onSelect={(footer) => {
                    createFooterFunc(footer);
                    dispatch(setGlobalPopupElement(null));
                }}
                onSkip={() => {
                    createFooterFunc();
                    dispatch(setGlobalPopupElement(null));
                }}
            />
        ))
    };

    // Handle make default
    const handleMakeDefault = (footer: CustomFooterItem): void => {
        // Logic to make header default
        dispatch(setGlobalPopupElement(
            <MakeDefaultModal
                isOpen={true}
                componentData={footer}
                componentType="footer"
                onClose={() => {
                    dispatch(setGlobalPopupElement(null));
                }}
                onConfirm={async (id, screenMode) => {
                    try {
                        await makeFooterDefault(id, screenMode);
                        // Update the state to reflect the change
                        loadFooters();

                        toastMe("Header set as default!", "success");
                        dispatch(setGlobalPopupElement(null));
                    } catch (error) {
                        console.error("Error making header default:", error);
                        toastMe(
                            "Failed to make header default. Please try again.",
                            "error"
                        );
                    }
                }}
            />
        ))
    };

    // Handle delete
    const handleDelete = async (id: string): Promise<void> => {
        // eslint-disable-next-line no-restricted-globals
        if (confirm("Are you sure you want to delete this footer?")) {
            try {
                await deleteCustomFooter(id);
                setFooters((prevFooters) =>
                    prevFooters.filter((footer) => footer._id !== id)
                );
            } catch (error) {
                console.error("Error deleting footer:", error);
                toastMe("Failed to delete footer. Please try again.", "error");
            }
        }
    };

    // Handle edit - navigate to edit page
    const handleEdit = (id: string): void => {
        navigate(`/footer-builder/${id}`);
    };

    return (
        <div className="p-6 max-w-6xl mx-auto">
            <h1 className="text-2xl font-bold mb-6">Footer Management</h1>

            {/* Search Panel */}
            <div className="bg-gray-100 p-4 mb-6 rounded-lg">
                <div className="flex items-center">
                    <div className="relative flex-grow">
                        <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                            <Search className="w-5 h-5 text-gray-400" />
                        </div>
                        <input
                            type="text"
                            className="bg-white border border-gray-300 p-2 pl-10 rounded-md w-full"
                            placeholder="Search footers by name or description..."
                            value={searchTerm}
                            onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                            ) => setSearchTerm(e.target.value)}
                        />
                    </div>
                </div>
            </div>

            {/* Create New Footer Form */}
            <div className="bg-white p-4 mb-6 border rounded-lg shadow-sm">
                <h2 className="text-lg font-semibold mb-3">
                    Create New Footer
                </h2>
                <form
                    onSubmit={handleCreateFooter}
                    className="flex items-end gap-3">
                    <div className="flex-grow">
                        <label
                            htmlFor="footerName"
                            className="block text-sm font-medium mb-1">
                            Footer Name
                        </label>
                        <input
                            type="text"
                            id="footerName"
                            className="bg-white border border-gray-300 p-2 rounded-md w-full"
                            value={newFooterName}
                            onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                            ) => setNewFooterName(e.target.value)}
                            required
                        />
                    </div>
                    <button
                        type="submit"
                        className="bg-blue-600 text-white px-4 py-2 rounded-md flex items-center"
                        disabled={isSubmitting}>
                        <Plus className="w-4 h-4 mr-1" />
                        {isSubmitting ? "Creating..." : "Create"}
                    </button>
                </form>
            </div>

            {/* Footers List */}
            <div className="bg-white border rounded-lg shadow-sm overflow-hidden">
                <h2 className="sr-only">Footers List</h2>
                {isLoading ? (
                    <div className="p-8 text-center">Loading footers...</div>
                ) : footers.length === 0 ? (
                    <div className="p-8 text-center">
                        No footers found. Create a new one!
                    </div>
                ) : (
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Footer
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Description
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                            {footers.map((footer) => (
                                <tr key={footer._id}>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center">
                                            {footer.images ? (
                                                <img
                                                    src={getImageUrl(footer.images)}
                                                    alt={footer.name}
                                                    className="w-10 h-10 rounded-md mr-3 bg-gray-200"
                                                />
                                            ) : (
                                                <div className="w-10 h-10 rounded-md mr-3 bg-gray-200"></div>
                                            )}
                                            <div className="flex items-center">
                                                <span className="font-medium">
                                                    {footer.name}
                                                </span>
                                                {footer.isFrontHeader && (
                                                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                                        <Star className="w-3 h-3 mr-1" />
                                                        Default: {footer.screenMode === 'all' ? 'Desktop & Mobile' : footer.screenMode}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <p className="text-sm text-gray-500">
                                            {footer.description ||
                                                "No description available"}
                                        </p>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                        <div className="flex justify-end space-x-2">
                                            <button
                                                onClick={() => handleMakeDefault(footer)}
                                                className="px-2 py-1 border border-blue-600 text-blue-600 rounded-xl"
                                            >
                                                Set as Default Footer
                                            </button>

                                            <button
                                                onClick={() =>
                                                    handleDelete(footer._id)
                                                }
                                                className="text-red-600 hover:text-red-900"
                                                title="Delete footer">
                                                <Trash className="w-4 h-4" />
                                            </button>

                                            <button
                                                onClick={() =>
                                                    handleEdit(footer._id)
                                                }
                                                className="text-blue-600 hover:text-blue-900"
                                                title="Edit footer">
                                                <Pencil className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}

                {/* Pagination */}
                {!isLoading && footers.length > 0 && (
                    <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                        <div className="flex-1 flex justify-between sm:hidden">
                            <button
                                onClick={() => setCurrentPage(currentPage - 1)}
                                disabled={currentPage === 1}
                                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-500">
                                Previous
                            </button>
                            <button
                                onClick={() => setCurrentPage(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:bg-gray-100 disabled:text-gray-500">
                                Next
                            </button>
                        </div>
                        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                            <div>
                                <p className="text-sm text-gray-700">
                                    Showing{" "}
                                    <span className="font-medium">
                                        {(currentPage - 1) * itemsPerPage + 1}
                                    </span>{" "}
                                    to{" "}
                                    <span className="font-medium">
                                        {Math.min(
                                            currentPage * itemsPerPage,
                                            totalPages * itemsPerPage
                                        )}
                                    </span>{" "}
                                    of{" "}
                                    <span className="font-medium">
                                        {totalPages * itemsPerPage}
                                    </span>{" "}
                                    results
                                </p>
                            </div>
                            <div>
                                <nav
                                    className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                                    aria-label="Pagination">
                                    <button
                                        onClick={() =>
                                            setCurrentPage(currentPage - 1)
                                        }
                                        disabled={currentPage === 1}
                                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100">
                                        <span className="sr-only">
                                            Previous
                                        </span>
                                        {/* Add previous icon here */}
                                    </button>
                                    {/* Page numbers */}
                                    {[...Array(totalPages)].map((_, index) => (
                                        <button
                                            key={index + 1}
                                            onClick={() =>
                                                setCurrentPage(index + 1)
                                            }
                                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === index + 1
                                                ? "z-10 bg-blue-50 border-blue-500 text-blue-600"
                                                : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                                                }`}>
                                            {index + 1}
                                        </button>
                                    ))}
                                    <button
                                        onClick={() =>
                                            setCurrentPage(currentPage + 1)
                                        }
                                        disabled={currentPage === totalPages}
                                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100">
                                        <span className="sr-only">Next</span>
                                        {/* Add next icon here */}
                                    </button>
                                </nav>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FooterManagementPage;
